import React, {useMemo} from 'react';
import {RefreshCw} from 'lucide-react';
import {useTokenRefreshState} from '../../graphql/localState';

const formatTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export function TokenRefreshIndicator() {
  const tokenState = useTokenRefreshState();

  // Calculate time until refresh based on token expiry from Apollo cache
  const timeUntilRefresh = useMemo(() => {
    if (!tokenState.tokenExpiry) return null;

    const now = Date.now();
    const timeLeft = tokenState.tokenExpiry - now;
    const bufferTime = 5 * 60 * 1000; // 5 minutes buffer (when refresh happens)
    const timeUntilRefreshStarts = timeLeft - bufferTime;

    return timeUntilRefreshStarts <= 0 ? 0 : timeUntilRefreshStarts;
  }, [tokenState.tokenExpiry]);

  // Calculate percentage for progress bar
  const percentage = useMemo(() => {
    if (timeUntilRefresh === null || timeUntilRefresh <= 0) return 0;
    return Math.min(100, (timeUntilRefresh / (25 * 60 * 1000)) * 100); // 25 minutes = 100%
  }, [timeUntilRefresh]);

  // Don't render if no token
  if (timeUntilRefresh === null) {
    return null;
  }

  const getColor = () => {
    if (tokenState.isRefreshing) return 'text-green-600 dark:text-green-400';
    if (timeUntilRefresh <= 60000) return 'text-red-600 dark:text-red-400'; // < 1 minute
    if (timeUntilRefresh <= 300000) return 'text-orange-600 dark:text-orange-400'; // < 5 minutes
    return 'text-gray-600 dark:text-gray-400';
  };

  const getBarColor = () => {
    if (tokenState.isRefreshing) return 'bg-green-500';
    if (timeUntilRefresh <= 60000) return 'bg-red-500';
    if (timeUntilRefresh <= 300000) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  return (
    <div className="flex items-center space-x-3">
      {/* Countdown and Icon */}
      <div className="flex items-center space-x-2">
        <RefreshCw size={14} className={`${getColor()} ${tokenState.isRefreshing ? 'animate-spin' : ''}`} />
        <span className={`text-xs font-mono ${getColor()}`}>
          {tokenState.isRefreshing ? 'Refreshing token...' : timeUntilRefresh <= 0 ? 'Token expired' : <>Refresh in {formatTime(timeUntilRefresh)}</>}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="hidden sm:flex items-center space-x-2 flex-1 max-w-xs">
        <div className="flex-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className={`h-full ${getBarColor()} transition-all duration-1000 ease-linear`} style={{width: `${percentage}%`}} />
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">{Math.round(percentage)}%</span>
      </div>
    </div>
  );
}
